import { useEffect, useRef } from "react";

import { Stack, Typography } from "@mui/material";

import { useInboundNetworkInvites, useRespondToNetworkInvite } from "hooks";

import { InboundInviteCard } from "./components/InboundInviteCard";

import type { InviteTargetType } from "../../types";

interface LastResponse {
  action: "accept" | "decline";
  inviteTargetType: InviteTargetType;
}

interface Props {
  onNotification: (
    message: string,
    severity: "success" | "info",
    inviteTargetType: InviteTargetType,
  ) => void;
}

export function InboundInvites(props: Props) {
  const { onNotification } = props;
  const { data, loading, error, refetch } = useInboundNetworkInvites();
  const {
    loading: respondLoading,
    error: respondError,
    success: respondSuccess,
    respondToInvite,
    resetState,
  } = useRespondToNetworkInvite();
  const lastResponseRef = useRef<LastResponse | null>(null);

  const handleRespond = (
    inviteId: number,
    inviteTargetType: InviteTargetType,
    accepted: boolean,
  ) => {
    lastResponseRef.current = {
      action: accepted ? "accept" : "decline",
      inviteTargetType,
    };
    respondToInvite(inviteId, inviteTargetType, accepted);
  };

  useEffect(() => {
    if (respondSuccess && lastResponseRef.current) {
      const { action, inviteTargetType } = lastResponseRef.current;
      const message =
        action === "accept" ? "Invite accepted" : "Invite declined";
      const severity = action === "accept" ? "success" : "info";
      onNotification(message, severity, inviteTargetType);
      refetch();
      lastResponseRef.current = null;
      resetState();
    }
  }, [respondSuccess, refetch, resetState, onNotification]);

  // Don't render if loading or no data
  if (loading || data.length === 0) {
    return null;
  }

  if (error) {
    return (
      <Stack spacing={2}>
        <Typography variant="h5" fontWeight="600">
          Inbound Invites
        </Typography>
        <Typography color="error">{error}</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h5" fontWeight="600">
        Inbound Invites ({data.length})
      </Typography>
      {respondError && (
        <Typography color="error" variant="body2">
          {respondError}
        </Typography>
      )}
      <Stack spacing={2}>
        {data.map((invite) => (
          <InboundInviteCard
            key={`${invite.invite_target_type}-${invite.invite_id}`}
            invite={invite}
            onRespond={handleRespond}
            loading={respondLoading}
          />
        ))}
      </Stack>
    </Stack>
  );
}
